import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNull } from 'lodash';
import { useTranslation } from 'react-i18next';
import { RoutesList } from 'routes';
// Import Components
import { Notification } from 'components/Header/types';
// Import Helpers
import { getFilterHolderKey, parseNotificationContentMessage } from 'services/Helpers';
// Import Services
import { NotificationHttpService } from 'services/http';
// Import Config
import { FilterHolderFieldsConfigs as CustomerJourneyConfigs } from 'pages/CustomerJourneyListPage/configs';
// Import Hooks
import useGoToRoute from './GoToRoute';
import { useDesktopNotifications } from './UseDesktopNotifications';
import useNotificationPolling from './UseNotificationPolling';
import useRequest from './UseRequest';
import useToaster from './UseToaster';
import useQueryParams from './UseQueryParams';
// Import Constants
import { AlertTypes, CustomerJourneyStatusLabels, RealtimePromotionTypes, l } from 'constants/common';
// Import Enums
import { ObjectTypeIdsEnum } from 'enums';

const useNotifications = () => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const { goToRoute } = useGoToRoute();
    const { encode } = useQueryParams();
    const { showToaster } = useToaster();
    const { showDesktopNotification } = useDesktopNotifications();

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const handleNewNotifications = useCallback(
        (newNotifications: Notification[]) => {
            if (newNotifications.length === 0) return;

            const defaultMessage = t(l.NewNotification);
            const isActiveTab = document.visibilityState === 'visible' && document.hasFocus();

            for (const notification of newNotifications) {
                const message = parseNotificationContentMessage(notification, t) || defaultMessage;

                if (isActiveTab) {
                    showToaster(AlertTypes.info, message);
                    continue;
                }

                showDesktopNotification(defaultMessage, {
                    body: message,
                    tag: `notification-${notification.EventLogId}`,
                    icon: '/crm-logo-light.svg',
                });
            }
        },
        [showDesktopNotification, showToaster, t],
    );

    const { isLoading, publishCurrentState } = useNotificationPolling(
        setNotifications,
        setUnreadCount,
        handleNewNotifications,
    );

    useEffect(() => {
        const baseTitle = 'Bet Construct';
        document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;

        return () => {
            document.title = baseTitle;
        };
    }, [unreadCount]);

    const { markAsReadRequest, markAsUnreadRequest, markAllAsReadRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- fire-and-forget with optimistic update
            markAsReadRequest: NotificationHttpService.markAsRead(),
            // eslint-disable-next-line no-restricted-syntax -- fire-and-forget with optimistic update
            markAsUnreadRequest: NotificationHttpService.markAsUnread(),
            // eslint-disable-next-line no-restricted-syntax -- fire-and-forget with optimistic update
            markAllAsReadRequest: NotificationHttpService.markAllAsRead(),
        }),
        [],
    );

    const markAsRead = (notification: Notification) => {
        doPostRequest(markAsReadRequest.request, {
            requestBody: [notification.EventLogId],
            errorCallback: (error) => {
                console.error(error);
            },
        });

        const nextNotifications = notifications.map((n) =>
            n.EventLogId === notification.EventLogId ? { ...n, ReadDate: new Date().toString() } : n,
        );

        const nextUnreadCount = nextNotifications.filter((n) => !n.ReadDate).length;

        setNotifications(nextNotifications);
        setUnreadCount(nextUnreadCount);

        publishCurrentState(nextNotifications, nextUnreadCount);
    };

    const markAsUnread = (notification: Notification) => {
        doPostRequest(markAsUnreadRequest.request, {
            requestBody: [notification.EventLogId],
            errorCallback: (error) => {
                console.error(error);
            },
        });
        const nextNotifications = notifications.map((n) =>
            n.EventLogId === notification.EventLogId ? { ...n, ReadDate: null } : n,
        );
        const nextUnreadCount = nextNotifications.filter((n) => !n.ReadDate).length;

        setNotifications(nextNotifications);
        setUnreadCount(nextUnreadCount);
        publishCurrentState(nextNotifications, nextUnreadCount);
    };

    const markAllAsRead = () => {
        doPostRequest(markAllAsReadRequest.request, {
            errorCallback: (error) => {
                console.error(error);
            },
        });
        const nextNotifications = notifications.map((n) => ({ ...n, ReadDate: new Date().toString() }));
        const nextUnreadCount = 0;

        setNotifications(nextNotifications);
        setUnreadCount(nextUnreadCount);
        publishCurrentState(nextNotifications, nextUnreadCount);
    };

    const redirectOnClick = (notification: Notification) => {
        const item = notification.ListItem;
        if (isNull(item)) {
            return;
        }

        switch (item.ObjectTypeId) {
            case ObjectTypeIdsEnum.Campaign:
                goToRoute(RoutesList.CAMPAIGN_REPORT, {
                    campaignId: item.Id.toString(),
                });
                return;

            case ObjectTypeIdsEnum.RealtimePromotion: {
                const isViewPageAvailable = ![
                    CustomerJourneyStatusLabels.Updating,
                    CustomerJourneyStatusLabels.Starting,
                    CustomerJourneyStatusLabels.Resuming,
                    CustomerJourneyStatusLabels.Suspending,
                    CustomerJourneyStatusLabels.Invalid,
                ].includes(item.Status.toString());

                if (isViewPageAvailable) {
                    const isActionCampaign = item.PromotionType.toString() === RealtimePromotionTypes.ActionCampaign;

                    const route = isActionCampaign ? RoutesList.REALTIME_ACTION_VIEW : RoutesList.REALTIME_JOURNEY_VIEW;

                    goToRoute(route, {
                        workflowId: item.Id,
                    });
                } else {
                    const params = encode({
                        [getFilterHolderKey(l.JourneyPage)]: {
                            [CustomerJourneyConfigs.idFieldName]: item.Id.toString(),
                        },
                    });

                    goToRoute({ ...RoutesList.REALTIME_PROMOTIONS_LIST }, null, params);
                }

                return;
            }

            case ObjectTypeIdsEnum.Report: {
                goToRoute(RoutesList.REPORT_EXECUTED_LIST, {
                    reportId: item.Id,
                    reportType: item.ReportType,
                });
                return;
            }

            case ObjectTypeIdsEnum.ObjectApproval: {
                goToRoute(RoutesList.OBJECT_APPROVAL_REQUEST_VIEW, {
                    approvalRequestId: item.Id,
                });
                return;
            }

            default:
                return;
        }
    };

    return {
        isLoading,
        notifications,
        unreadCount,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        setNotifications,
        setUnreadCount,
        redirectOnClick,
    };
};

export default useNotifications;
